/**
 * AcmImportModal — přidání existujících ACM projektů do Codable.
 *
 * Není to „import kódu od nuly“: na lokálním ACM jde o adopci / registraci
 * projektů, které už běží (orchestrátor env / apphost), ale Codable je ještě
 * nevidí. Z mesh peeru = mirror (edit na dálku) nebo clone (běh u nás).
 */
import { GitlabConnectionSelect, GitlabImportAccess, ProjectWorkerChoice, type ImportMode } from './GitlabImportAccess';
import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  Copy,
  Download,
  Globe,
  HardDrive,
  Loader2,
  Network,
  Search,
  Server,
} from 'lucide-react';
import { api } from '../../../lib/api';
import { Badge, Button, Input, Label, Modal } from '../../../components/ui';
import { normalizeMeshPeers, type MeshPeer } from '../../../widgets/NodePicker';

interface RemoteProject {
  gitlab_project_key?: string;
  id: string;
  slug: string;
  name: string;
  description: string | null;
  frontend_domain: string | null;
  backend?: { kind?: string } | null;
  already_imported?: boolean;
}

interface LocalCandidate {
  kind: 'orch_env' | 'apphost';
  id: string;
  env_name?: string;
  apphost_id?: string;
  name: string;
  status: string;
  tech: {
    frontend: string | null;
    backend: string;
    git_repo: string | null;
    git_host: string | null;
    on_fleet_gitlab: boolean;
  };
  source_hint?: 'lovable' | null;
  domain?: string | null;
  already_imported: boolean;
  importable: boolean;
  reason?: string | null;
}

interface DiscoverResp {
  ok: boolean;
  error?: string;
  projects?: RemoteProject[];
}

type SourceSel =
  | { local: true }
  | { node: string }
  | { credential_vault_id: string }
  | null;

interface VaultConnection {
  id: string;
  label: string;
  preview: string | null;
  metadata: { kind?: string; mcp_url?: string };
  updated_at: string;
}

function peerStatusDot(peer: MeshPeer): 'green' | 'gray' | 'yellow' {
  if (peer.online === true || peer.status === 'online') return 'green';
  if (peer.online === false || peer.status === 'offline') return 'gray';
  return 'yellow';
}

export default function AcmImportModal(
  { onClose, onImported }: { onClose: () => void; onImported: () => void },
) {
  const { t } = useTranslation();
  const [importModes, setImportModes] = useState<Record<string, ImportMode>>({});
  const [gitlabConnection, setGitlabConnection] = useState('');
  const [completedSlug, setCompletedSlug] = useState('');
  const [operationId, setOperationId] = useState('');
  const operation = useQuery({ queryKey: ['import-operation', operationId], enabled: !!operationId,
    queryFn: () => api.mcp<{ ok: boolean; operation?: { status: string; slug: string; error?: string } }>('orchestrator.project_operation_get', { operation_id: operationId }),
    refetchInterval: (q) => ['queued','running'].includes(q.state.data?.operation?.status ?? 'queued') ? 2000 : false });
  const [source, setSource] = useState<SourceSel>({ local: true });
  const [manualEp, setManualEp] = useState('');
  const [manualKey, setManualKey] = useState('');
  const [showManual, setShowManual] = useState(false);
  const [peerSearch, setPeerSearch] = useState('');

  const meQ = useQuery({
    queryKey: ['identity-me'],
    queryFn: () => api.mcp<{ ok: boolean; identity?: { id: string } }>('identity.me'),
    staleTime: 60_000,
  });
  const entityId = meQ.data?.identity?.id ?? '';
  const vaultQ = useQuery({
    queryKey: ['identity-vault', entityId, 'acm_connection'],
    queryFn: () =>
      api.mcp<{ ok: boolean; items?: VaultConnection[] }>('identity.vault_list', {
        entity_id: entityId,
        namespace: 'acm_connection',
      }),
    enabled: showManual && !!entityId,
    staleTime: 30_000,
  });
  const vaultConnections = vaultQ.data?.items ?? [];
  const saveConnectionMut = useMutation({
    mutationFn: async () => {
      if (!entityId) throw new Error(t('codable.noIdentityEntity'));
      const parsed = new URL(manualEp.trim());
      if (
        parsed.protocol !== 'https:' || parsed.pathname !== '/mcp' || parsed.search ||
        parsed.hash || parsed.username || parsed.password
      ) {
        throw new Error(t('codable.mcpEndpointInvalid'));
      }
      const label = `acm/${parsed.hostname}${parsed.port ? `:${parsed.port}` : ''}`;
      const result = await api.mcp<{ ok: boolean; id?: string; error?: string }>(
        'identity.vault_put',
        {
          entity_id: entityId,
          namespace: 'acm_connection',
          label,
          payload: { mcp_url: parsed.toString(), api_key: manualKey },
          metadata: { kind: 'acm_mcp', mcp_url: parsed.toString() },
          preview: `${parsed.hostname} • ${manualKey.slice(0, 8)}…`,
          access_scope: 'user_and_node',
        },
      );
      if (!result.ok || !result.id) throw new Error(result.error ?? t('codable.vaultWriteFailed'));
      return result;
    },
    onSuccess: async (result) => {
      setManualKey('');
      setSource({ credential_vault_id: result.id! });
      await vaultQ.refetch();
      toast.success(t('codable.connectionStored'));
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const peersQ = useQuery({
    queryKey: ['mesh-peers-list', 'import-sources'],
    queryFn: async () => {
      const r = await api.mcp<unknown>('mesh.peers_list', { include_local: true });
      const nodes = normalizeMeshPeers(r);
      const localIds = new Set<string>();
      for (const n of nodes) {
        if (n.is_local) {
          if (n.name) localIds.add(n.name);
          if (n.host) localIds.add(n.host);
        }
      }
      return nodes.filter((n) =>
        !n.is_local && n.enabled !== false &&
        !localIds.has(n.name) && !(n.host && localIds.has(n.host))
      );
    },
    staleTime: 60_000,
  });

  const peers = useMemo(() => {
    const list = peersQ.data ?? [];
    const q = peerSearch.trim().toLowerCase();
    if (!q) return list;
    return list.filter((n) =>
      (n.label || n.name).toLowerCase().includes(q) ||
      (n.host ?? '').toLowerCase().includes(q) ||
      n.name.toLowerCase().includes(q)
    );
  }, [peersQ.data, peerSearch]);

  const isLocal = !!source && 'local' in source;
  const selectedPeerName = source && 'node' in source ? source.node : null;

  const discoverQ = useQuery({
    queryKey: ['codable-acm-discover', JSON.stringify(source)],
    enabled: !!source,
    queryFn: () =>
      isLocal
        ? api.mcp<DiscoverResp>('codable.local_discover', {})
        : api.mcp<DiscoverResp>('codable.acm_discover', { ...(source ?? {}) }),
    retry: 0,
  });

  const importMut = useMutation({
    mutationFn: (a: { remote_slug: string; mode: 'clone' | 'mirror' }) =>
      api.mcp<{ ok: boolean; error?: string; slug?: string; mode?: string; operation_id?: string }>('codable.acm_import', {
        ...(source ?? {}),
        ...a, import_mode: importModes[a.remote_slug] ?? 'existing',
      }),
    onSuccess: (r) => {
      if (r.ok) {
        toast.success(t('codable.importOkSlugMode', { slug: r.slug, mode: r.mode }));
        if (r.operation_id) setOperationId(r.operation_id); else if (r.slug) setCompletedSlug(r.slug);
      } else toast.error(t('codable.importFailedDetail', { error: r.error ?? 'unknown' }));
    },
    onError: (e: Error) => toast.error(t('codable.importErrorDetail', { msg: e.message })),
  });

  const localImportMut = useMutation({
    mutationFn: (a: { env_name?: string; apphost_id?: string; import_mode?: ImportMode }) =>
      api.mcp<{
        ok: boolean;
        error?: string;
        slug?: string;
        mirrored?: boolean;
        note?: string;
        warning?: string; operation_id?: string;
      }>('codable.local_import', { ...a, connection_id: gitlabConnection || undefined }),
    onSuccess: (r) => {
      if (r.ok) {
        toast.success(
          r.mirrored
            ? t('codable.adoptOkMirrored', { slug: r.slug })
            : t('codable.adoptOk', { slug: r.slug }),
        );
        if (r.warning) toast.error(r.warning, { duration: 12_000 });
        if (r.operation_id) setOperationId(r.operation_id); else if (r.slug) setCompletedSlug(r.slug);
      } else toast.error(t('codable.importFailedDetail', { error: r.error ?? 'unknown' }));
    },
    onError: (e: Error) => toast.error(t('codable.importErrorDetail', { msg: e.message })),
  });

  const projects = discoverQ.data?.ok && !isLocal ? (discoverQ.data.projects ?? []) : [];
  const localProjects = discoverQ.data?.ok && isLocal
    ? ((discoverQ.data.projects ?? []) as unknown as LocalCandidate[])
    : [];

  const sourceSummary = isLocal
    ? t('codable.localSource')
    : selectedPeerName
    ? selectedPeerName
    : source && 'credential_vault_id' in source
    ? t('codable.vaultConnection')
    : '—';

  return (
    <Modal open onClose={onClose} title={t('codable.importTitle')} size='xl'>
      <div className='space-y-5 min-w-0'>
        <p className='text-sm text-gray-400 leading-relaxed'>
          {t('codable.importAcmIntro')}
        </p>

        {/* ── Zdroj ─────────────────────────────────────────────────────── */}
        <div className='space-y-3'>
          <div className='text-[10px] font-semibold text-gray-600 uppercase tracking-widest'>
            {t('codable.sourceLabel')}
          </div>

          {/* Tento ACM — výrazná karta */}
          <button
            type='button'
            onClick={() => {
              setSource({ local: true });
              setShowManual(false);
            }}
            className={`w-full text-left rounded-2xl border p-3.5 transition-colors ${
              isLocal
                ? 'border-blue-500/50 bg-blue-600/10 ring-1 ring-blue-500/30'
                : 'border-gray-800 bg-gray-950/50 hover:border-gray-700'
            }`}
          >
            <div className='flex items-start gap-3'>
              <div className={`rounded-xl p-2.5 shrink-0 ${isLocal ? 'bg-blue-500/20 text-blue-300' : 'bg-gray-800 text-gray-400'}`}>
                <HardDrive size={18} />
              </div>
              <div className='min-w-0 flex-1'>
                <div className='text-sm font-semibold text-gray-100'>
                  {t('codable.localSource')}
                </div>
                <p className='text-xs text-gray-500 mt-0.5 leading-relaxed'>
                  {t('codable.localSourceHint')}
                </p>
              </div>
              {isLocal && <Badge variant='blue'>{t('codable.selected')}</Badge>}
            </div>
          </button>

          {/* Mesh peery */}
          <div className='rounded-2xl border border-gray-800 bg-gray-950/40 p-3 space-y-2.5'>
            <div className='flex flex-wrap items-center justify-between gap-2'>
              <div className='text-xs font-medium text-gray-300 flex items-center gap-1.5'>
                <Network size={13} className='text-gray-500' />
                {t('codable.meshPeersSource')}
              </div>
              {(peersQ.data?.length ?? 0) > 4 && (
                <div className='relative w-full sm:w-52'>
                  <Search size={12} className='absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-600' />
                  <input
                    className='w-full bg-gray-900 border border-gray-800 rounded-lg pl-8 pr-2 py-1.5 text-xs text-gray-200 placeholder-gray-600 focus:border-blue-500 focus:outline-none'
                    placeholder={t('codable.searchNodes')}
                    value={peerSearch}
                    onChange={(e) => setPeerSearch(e.target.value)}
                  />
                </div>
              )}
            </div>
            <p className='text-[11px] text-gray-600'>{t('codable.meshPeersHint')}</p>

            {peersQ.isLoading && (
              <div className='flex items-center gap-2 text-xs text-gray-500 py-2'>
                <Loader2 size={12} className='animate-spin' /> {t('codable.loadingNodes')}
              </div>
            )}
            {!peersQ.isLoading && peers.length === 0 && (
              <div className='text-xs text-gray-600 py-1'>
                {peerSearch ? t('codable.noNodesMatch') : t('codable.noMeshPeers')}
              </div>
            )}
            <div className='grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-48 overflow-auto'>
              {peers.map((n) => {
                const active = selectedPeerName === n.name;
                const dot = peerStatusDot(n);
                return (
                  <button
                    key={n.name}
                    type='button'
                    onClick={() => {
                      setSource({ node: n.name });
                      setShowManual(false);
                    }}
                    className={`flex items-center gap-2.5 rounded-xl border px-3 py-2.5 text-left transition-colors ${
                      active
                        ? 'border-blue-500/50 bg-blue-600/10'
                        : 'border-gray-800 bg-gray-900/50 hover:border-gray-700'
                    }`}
                  >
                    <span
                      className={`w-2 h-2 rounded-full shrink-0 ${
                        dot === 'green'
                          ? 'bg-emerald-400'
                          : dot === 'gray'
                          ? 'bg-gray-600'
                          : 'bg-amber-400'
                      }`}
                      title={n.status ?? (n.online === true ? 'online' : n.online === false ? 'offline' : 'unknown')}
                    />
                    <div className='min-w-0 flex-1'>
                      <div className='text-sm text-gray-100 truncate font-medium'>
                        {n.label || n.name}
                      </div>
                      <div className='text-[10px] font-mono text-gray-600 truncate'>
                        {n.host || n.name}
                      </div>
                    </div>
                    {active && <Badge variant='blue' className='text-[9px]'>{t('codable.selected')}</Badge>}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Jiný endpoint */}
          <button
            type='button'
            onClick={() => setShowManual((v) => !v)}
            className={`w-full flex items-center gap-2 px-3 py-2.5 rounded-xl border text-sm transition-colors ${
              showManual || (source && 'credential_vault_id' in source)
                ? 'border-blue-500/40 bg-blue-600/10 text-blue-300'
                : 'border-dashed border-gray-700 text-gray-500 hover:text-gray-300 hover:border-gray-600'
            }`}
          >
            <Globe size={14} /> {t('codable.otherEndpoint')}
          </button>
          {showManual && (
            <div className='space-y-3 rounded-xl border border-gray-800 bg-gray-950/40 p-3'>
              {vaultConnections.length > 0 && (
                <div className='space-y-1.5'>
                  <div className='text-[10px] uppercase tracking-wide text-gray-600 font-semibold'>
                    {t('codable.savedConnections')}
                  </div>
                  <div className='flex flex-wrap gap-2'>
                    {vaultConnections.map((connection) => (
                      <button
                        key={connection.id}
                        type='button'
                        onClick={() => setSource({ credential_vault_id: connection.id })}
                        className={`px-3 py-2 rounded-lg border text-xs ${
                          source && 'credential_vault_id' in source &&
                            source.credential_vault_id === connection.id
                            ? 'border-blue-500 bg-blue-600/15 text-blue-300'
                            : 'border-gray-800 text-gray-400 hover:text-white'
                        }`}
                      >
                        {connection.preview || connection.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div className='grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2 items-end'>
                <div>
                  <Label>{t('codable.endpointField')}</Label>
                  <Input
                    value={manualEp}
                    onChange={(e) => setManualEp(e.target.value)}
                    placeholder='https://node.example.cz/mcp'
                  />
                </div>
                <div>
                  <Label>{t('codable.apiKey')}</Label>
                  <Input
                    value={manualKey}
                    onChange={(e) => setManualKey(e.target.value)}
                    placeholder='adx_…'
                    type='password'
                  />
                </div>
                <Button
                  variant='secondary'
                  size='sm'
                  className='w-full sm:w-auto'
                  disabled={!manualEp.trim() || !manualKey.trim() || saveConnectionMut.isPending}
                  onClick={() => saveConnectionMut.mutate()}
                >
                  {t('codable.storeAndConnect')}
                </Button>
              </div>
              <p className='text-[11px] text-gray-600'>{t('codable.connectionVaultHint')}</p>
            </div>
          )}
        </div>

        {/* ── Projekty ──────────────────────────────────────────────────── */}
        {operationId && <p className='text-sm'>{operation.data?.operation?.status} · {operation.data?.operation?.error}</p>}
        {(completedSlug || operation.data?.operation?.status === 'completed') && <><ProjectWorkerChoice slug={completedSlug || operation.data!.operation!.slug} /><Button onClick={onImported}>{t('common.done', 'Hotovo')}</Button></>}
        {isLocal && <GitlabConnectionSelect value={gitlabConnection} onChange={setGitlabConnection} />}
        {source && (
          <div className='space-y-2 border-t border-gray-800 pt-4'>
            <div className='flex flex-wrap items-center justify-between gap-2'>
              <div className='text-[10px] font-semibold text-gray-600 uppercase tracking-widest'>
                {isLocal ? t('codable.localCandidates') : t('codable.projectsOnSource')}
              </div>
              <span className='text-[11px] text-gray-600 font-mono truncate max-w-[14rem]' title={sourceSummary}>
                {sourceSummary}
              </span>
            </div>

            {isLocal && (
              <p className='text-[11px] text-gray-500 leading-relaxed'>
                {t('codable.localCandidatesHint')}
              </p>
            )}
            {!isLocal && (
              <p className='text-[11px] text-gray-500 leading-relaxed'>
                {t('codable.remoteProjectsHint')}
              </p>
            )}

            {discoverQ.isLoading && (
              <div className='flex items-center gap-2 text-sm text-gray-500 py-3'>
                <Loader2 size={14} className='animate-spin' />
                {isLocal ? t('codable.loadingLocal') : t('codable.loadingRemote')}
              </div>
            )}
            {discoverQ.data && !discoverQ.data.ok && (
              <div className='text-sm text-red-400 py-2 rounded-xl border border-red-500/20 bg-red-500/5 px-3'>
                {discoverQ.data.error}
              </div>
            )}
            {discoverQ.data?.ok && !isLocal && projects.length === 0 && (
              <div className='text-sm text-gray-500 py-4 text-center border border-dashed border-gray-800 rounded-xl'>
                {t('codable.noRemoteProjects')}
              </div>
            )}
            {discoverQ.data?.ok && isLocal && localProjects.length === 0 && (
              <div className='text-sm text-gray-500 py-4 text-center border border-dashed border-gray-800 rounded-xl'>
                {t('codable.noLocalCandidates')}
              </div>
            )}

            {isLocal && (
              <div className='space-y-1.5 max-h-[min(22rem,45vh)] overflow-auto'>
                {localProjects.map((p) => (
                  <div
                    key={p.id}
                    className={`flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 px-3 py-2.5 rounded-xl border border-gray-800 bg-gray-900/40 ${
                      !p.importable ? 'opacity-60' : ''
                    }`}
                  >
                    <HardDrive size={14} className='text-gray-500 shrink-0 hidden sm:block' />
                    <div className='min-w-0 flex-1'>
                      <div className='text-sm text-white truncate'>
                        {p.name}{' '}
                        <span className='text-gray-600 font-mono text-xs'>
                          ({p.kind === 'orch_env' ? t('codable.kindOrch') : t('codable.kindApphost')})
                        </span>
                      </div>
                      <div className='flex items-center gap-1 mt-0.5 flex-wrap'>
                        {p.tech.frontend && <Badge variant='blue'>{p.tech.frontend}</Badge>}
                        {p.tech.backend !== 'none' && (
                          <Badge variant='green'>{p.tech.backend}</Badge>
                        )}
                        {p.source_hint === 'lovable' && <Badge variant='purple'>Lovable</Badge>}
                        {p.tech.git_host && !p.tech.on_fleet_gitlab && (
                          <Badge variant='yellow'>{p.tech.git_host}</Badge>
                        )}
                        {p.already_imported && (
                          <Badge variant='yellow'>{t('codable.alreadyImported')}</Badge>
                        )}
                      </div>
                      {!p.importable && p.reason && (
                        <div className='text-[11px] text-gray-500 mt-0.5 truncate' title={p.reason}>
                          {p.reason}
                        </div>
                      )}
                    </div>
                    {p.tech.on_fleet_gitlab && p.tech.git_repo && <GitlabImportAccess
                      project={new URL(p.tech.git_repo).pathname.slice(1).replace(/\.git$/, '')} connection={gitlabConnection || undefined}
                      mode={importModes[p.id] ?? 'existing'} onMode={(mode) => setImportModes((old) => ({ ...old, [p.id]: mode }))} />}
                    <Button
                      size='sm'
                      variant='secondary'
                      className='w-full sm:w-auto shrink-0 justify-center'
                      icon={<Download size={13} />}
                      disabled={!p.importable || localImportMut.isPending}
                      onClick={() =>
                        localImportMut.mutate(
                          p.kind === 'orch_env'
                            ? { env_name: p.env_name, import_mode: importModes[p.id] ?? 'existing' }
                            : { apphost_id: p.apphost_id, import_mode: importModes[p.id] ?? 'existing' },
                        )}
                      title={p.kind === 'orch_env'
                        ? t('codable.adoptHint')
                        : t('codable.wrapHint')}
                    >
                      {t('codable.adoptAction')}
                    </Button>
                  </div>
                ))}
                {localImportMut.isPending && (
                  <div className='flex items-center gap-2 text-sm text-blue-300'>
                    <Download size={14} className='animate-pulse' /> {t('codable.importing')}
                  </div>
                )}
              </div>
            )}

            {!isLocal && (
              <div className='space-y-1.5 max-h-[min(22rem,45vh)] overflow-auto'>
                {projects.map((p) => (
                  <div
                    key={p.id}
                    className='flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 px-3 py-2.5 rounded-xl border border-gray-800 bg-gray-900/40'
                  >
                    <Server size={14} className='text-gray-500 shrink-0 hidden sm:block' />
                    <div className='min-w-0 flex-1'>
                      <div className='text-sm text-white truncate'>
                        {p.name}{' '}
                        <span className='text-gray-600 font-mono text-xs'>({p.slug})</span>
                      </div>
                      <div className='flex items-center gap-1 mt-0.5 flex-wrap'>
                        {p.backend?.kind && p.backend.kind !== 'none' && (
                          <Badge variant='green'>{p.backend.kind}</Badge>
                        )}
                        {p.already_imported && (
                          <Badge variant='yellow'>{t('codable.alreadyImported')}</Badge>
                        )}
                      </div>
                    </div>
                    {p.gitlab_project_key && <GitlabImportAccess
                      project={p.gitlab_project_key.split(':')[1]!} connection={p.gitlab_project_key.split(':')[0]}
                      mode={importModes[p.slug] ?? 'existing'} onMode={(mode) => setImportModes((old) => ({ ...old, [p.slug]: mode }))} />}
                    <div className='grid grid-cols-2 sm:flex gap-1.5 shrink-0'>
                      <Button
                        size='sm'
                        variant='ghost'
                        className='justify-center'
                        icon={<Globe size={13} />}
                        disabled={p.already_imported || importMut.isPending}
                        onClick={() => importMut.mutate({ remote_slug: p.slug, mode: 'mirror' })}
                        title={t('codable.mirrorHint')}
                      >
                        {t('codable.mirrorAction')}
                      </Button>
                      <Button
                        size='sm'
                        variant='secondary'
                        className='justify-center'
                        icon={<Copy size={13} />}
                        disabled={p.already_imported || importMut.isPending}
                        onClick={() => importMut.mutate({ remote_slug: p.slug, mode: 'clone' })}
                        title={t('codable.cloneHint')}
                      >
                        {t('codable.cloneAction')}
                      </Button>
                    </div>
                  </div>
                ))}
                {importMut.isPending && (
                  <div className='flex items-center gap-2 text-sm text-blue-300'>
                    <Download size={14} className='animate-pulse' /> {t('codable.importing')}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}
